// modules
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useDispatch, useSelector } from 'react-redux';

// local imports
import DropdownMenuItem from './DropdownMenuItem';
import { RootState } from '../../store';
import { setPrimaryNavOpen } from '../../store/mapSlice';
import { LINK_BLUE, TEXT_GRAY, LIGHT_GRAY, FOCUS_BLUE } from '../../constants/colors';

// Sibling NWDC pages: full S3 URL in dev (reachable host), proxy prefix in deployed builds
const cloudfrontPrefix = import.meta.env.DEV
  ? `${import.meta.env.VITE_NWDC_S3_URL}`
  : import.meta.env.VITE_CLOUDFRONT_PROXY_PREFIX;

// Interactive Map is the current page — always resolve through the proxy prefix
const interactiveMapPrefix = import.meta.env.VITE_CLOUDFRONT_PROXY_PREFIX;

type MenuItem = {
  label: string;
  link?: string;
  current?: boolean;
  submenu?: { label: string; link: string }[];
};

const PrimaryNavigation = () => {
  const dispatch = useDispatch();
  const isLargeScreen = useMediaQuery('(min-width:1024px)');

  // Get drawer state from Redux
  const drawerOpen = useSelector(
    (state: RootState) => state.map.primaryNavOpen
  );
  const sidePanelOpen = useSelector(
    (state: RootState) => state.map.sidePanelOpen
  );

  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  // Close drawer when side panel opens on small screens
  useEffect(() => {
    if (sidePanelOpen && window.innerWidth < 491 && drawerOpen) {
      dispatch(setPrimaryNavOpen(false));
    }
  }, [sidePanelOpen, drawerOpen, dispatch]);

  // Close primary nav when screen shrinks below 491px and both panels are open
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 491 && drawerOpen && sidePanelOpen) {
        dispatch(setPrimaryNavOpen(false));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawerOpen, sidePanelOpen, dispatch]);

  const toggleDrawer = () => {
    dispatch(setPrimaryNavOpen(!drawerOpen));
  };

  const handleToggleMenu = (menuKey: string) => {
    setExpandedMenu((prev) => (prev === menuKey ? null : menuKey));
  };

  const menuItems: MenuItem[] = [
    { label: 'NWDC Home', link: `${cloudfrontPrefix}/` },
    { label: 'Interactive Map', current: true },
    {
      label: 'Access Data',
      submenu: [
        {
          label: 'Subset & Download Tool',
          link: `${cloudfrontPrefix}/subset-download/`,
        },
        {
          label: 'Data File Directory',
          link: `${cloudfrontPrefix}/data-file-directory?path=data/`,
        },
        { label: 'Web Services', link: `${cloudfrontPrefix}/web-services/` },
      ],
    },
    {
      label: 'About the Data',
      submenu: [
        {
          label: 'Data Overview & Availability',
          link: `${cloudfrontPrefix}/data-overview/`,
        },
        { label: 'Data Catalog', link: `${cloudfrontPrefix}/data-catalog/` },
      ],
    },
    {
      label: 'Resources',
      submenu: [
        { label: 'Tutorials', link: `${cloudfrontPrefix}/tutorials/` },
        {
          label: 'Spatial Extent Viewer',
          link: `${cloudfrontPrefix}/spatial-extent-viewer/`,
        },
        { label: 'FAQ', link: `${cloudfrontPrefix}/faq/` },
        { label: 'Glossary', link: `${cloudfrontPrefix}/glossary/` },
      ],
    },
    {
      label: 'About Us',
      submenu: [
        { label: 'About NWDC', link: `${cloudfrontPrefix}/about-us/` },
        { label: 'Release Notes', link: `${cloudfrontPrefix}/release-notes/` },
        {
          label: 'News & Events',
          link: `${cloudfrontPrefix}/news-and-events/`,
        },
      ],
    },
    { label: 'Contact Us', link: `${cloudfrontPrefix}/contact-us/` },
  ];

  return (
    <>
      {isLargeScreen && (
        <AppBar
          component="nav"
          aria-label="Primary navigation"
          position="static"
          sx={{
            backgroundColor: LIGHT_GRAY,
            zIndex: 2400,
            height: '47px',
            width: '100%',
            boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.1)',
            borderBottom: '1px solid #D1D4D5',
          }}
        >
          <Toolbar
            variant="dense"
            sx={{
              display: 'flex',
              height: '47px',
              minHeight: '47px',
              width: '100%',
              maxWidth: '1024px',
              margin: '0 auto',
              px: 1,
              py: 0,
              paddingTop: '2px',
              paddingBottom: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 0, height: '100%', ml: -1.95 }}>
              {menuItems.map((item, index) => {
                if (item.current) {
                  return (
                    <Typography
                      key={index}
                      component="a"
                      href={`${interactiveMapPrefix}/interactive-map/`}
                      aria-current="page"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        color: LINK_BLUE,
                        fontSize: '1rem',
                        fontWeight: 700,
                        px: '1rem',
                        py: '1rem',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:focus': {
                          outline: `0.25rem solid ${FOCUS_BLUE}`,
                          outlineOffset: 0,
                          zIndex: 1,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: '1rem',
                          right: '1rem',
                          bottom: 0,
                          height: '4px',
                          backgroundColor: LINK_BLUE,
                        },
                      }}
                    >
                      {item.label}
                    </Typography>
                  );
                }
                if (!item.submenu) {
                  return (
                    <Button
                      aria-label={item.label}
                      role="link"
                      disableRipple
                      disableTouchRipple
                      key={index}
                      href={item.link}
                      sx={{
                        position: 'relative',
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        color: TEXT_GRAY,
                        fontSize: '1rem',
                        fontWeight: 700,
                        px: '1rem',
                        py: '1rem',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: LIGHT_GRAY,
                          color: LINK_BLUE,
                          '&::after': {
                            transform: 'scaleX(1)',
                            opacity: 1,
                          },
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: '1rem',
                          right: '1rem',
                          bottom: 0,
                          height: '4px',
                          backgroundColor: LINK_BLUE,
                          opacity: 0,
                        },
                        '&:focus': {
                          zIndex: 1,
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                }
                return (
                  <DropdownMenuItem
                    key={index}
                    label={item.label}
                    submenu={item.submenu}
                  />
                );
              })}
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {!isLargeScreen && (
        <Box data-testid="primary-navigation-menu-button">
          <Button
            component="span"
            onClick={toggleDrawer}
            aria-label="menu"
            sx={{
              color: '#ffffff',
              backgroundColor: LINK_BLUE,
              borderRadius: 0,
              p: 0,
              height: '48px',
              width: '59px',
            }}
          >
            Menu
          </Button>
          <Drawer
            hideBackdrop
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer}
            ModalProps={{
              keepMounted: true,
              disableEnforceFocus: true,
              disableAutoFocus: true,
              disableRestoreFocus: true,
              sx: { pointerEvents: 'none', zIndex: 2400 },
            }}
            slotProps={{
              paper: {
                sx: {
                  pointerEvents: 'auto',
                  width: 240,
                },
              },
            }}
          >
            <Box
              component="nav"
              aria-label="Primary navigation"
              sx={{ width: 240 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  aria-label="close menu"
                  role="button"
                  onClick={toggleDrawer}
                  sx={{ height: '48px', width: '48px', mb: 2, borderRadius: 0 }}
                >
                  <CloseIcon sx={{ color: '#1B1B1B' }} />
                </IconButton>
              </Box>

              <List sx={{ mx: 2, py: 0 }}>
                {menuItems.map((item, index) => {
                  const isOpen = expandedMenu === item.label;

                  if (item.current) {
                    return (
                      <React.Fragment key={index}>
                        <ListItem disablePadding sx={{ borderTop: '1px solid #DFE1E2' }}>
                          <ListItemButton
                            disableRipple
                            component="a"
                            href={`${interactiveMapPrefix}/interactive-map/`}
                            aria-current="page"
                            onClick={toggleDrawer}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              px: 2,
                              py: '0.75rem',
                              borderLeft: `4px solid ${LINK_BLUE}`,
                            }}
                          >
                            <ListItemText
                              primary={item.label}
                              sx={{
                                my: 0,
                                '& .MuiListItemText-primary': {
                                  color: LINK_BLUE,
                                  fontSize: '1.06rem',
                                  fontWeight: 700,
                                  lineHeight: 1.1,
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </React.Fragment>
                    );
                  }

                  if (!item.submenu) {
                    return (
                      <React.Fragment key={index}>
                        <ListItem disablePadding sx={{ borderTop: '1px solid #DFE1E2' }}>
                          <ListItemButton
                            disableRipple
                            component="a"
                            href={item.link}
                            onClick={toggleDrawer}
                            sx={{ py: '0.75rem', px: '1rem' }}
                          >
                            <ListItemText
                              primary={item.label}
                              sx={{
                                my: 0,
                                '& .MuiListItemText-primary': {
                                  color: TEXT_GRAY,
                                  fontSize: '1.06rem',
                                  lineHeight: 1.1,
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      </React.Fragment>
                    );
                  }

                  return (
                    <Box key={index}>
                      <ListItem disablePadding sx={{ borderTop: '1px solid #DFE1E2' }}>
                        <ListItemButton
                          disableRipple
                          onClick={() => handleToggleMenu(item.label)}
                          sx={{ py: '0.75rem', px: '1rem' }}
                        >
                          <ListItemText
                            primary={item.label}
                            sx={{
                              my: 0,
                              '& .MuiListItemText-primary': {
                                color: TEXT_GRAY,
                                fontSize: '1.06rem',
                                lineHeight: 1.1,
                              },
                            }}
                          />
                          {isOpen ? (
                            <RemoveIcon sx={{ fontSize: '1.25rem' }} />
                          ) : (
                            <AddIcon sx={{ fontSize: '1.25rem' }} />
                          )}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={isOpen} timeout={0} unmountOnExit>
                        <List
                          component="div"
                          disablePadding
                          sx={{ pl: 0, py: 0 }}
                        >
                          {item.submenu.map((subItem, subIndex) => (
                            <React.Fragment key={subIndex}>
                              <ListItem disablePadding sx={{ borderTop: '1px solid #DFE1E2' }}>
                                <ListItemButton
                                  disableRipple
                                  component="a"
                                  href={subItem.link}
                                  onClick={toggleDrawer}
                                  sx={{ py: '0.5rem', px: '1rem', pl: '2.5rem' }}
                                >
                                  <ListItemText
                                    primary={subItem.label}
                                    sx={{
                                      my: 0,
                                      '& .MuiListItemText-primary': {
                                        color: TEXT_GRAY,
                                        fontSize: '1rem',
                                        lineHeight: 1.1,
                                      },
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            </React.Fragment>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        </Box>
      )}
    </>
  );
};

export default PrimaryNavigation;
